import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dajtpp3bcsf0ijchcv5g";

const channels = [
  {
    icon: "ri-mail-send-line",
    title: "邮箱联系",
    value: "contact@52chigua.example.com",
    text: "适合正式的合作咨询与内容反馈，我们会在两个工作日内回复。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "线索投稿",
    value: "通过下方表单提交",
    text: "请尽量写明事件的基本情况与信息来源，我们会先做基础核实。",
  },
  {
    icon: "ri-error-warning-line",
    title: "内容纠错",
    value: "标注事件标题即可",
    text: "如果发现记录中的时间、来源或表述有误，欢迎指正，我们会尽快核对。",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "联系我们 | 线索投稿与内容纠错 - 52吃瓜网",
    description:
      "联系52吃瓜网：提交热点线索、指出内容错误或洽谈合作。也可以通过邮箱 contact@52chigua.example.com 与我们取得联系，我们会认真对待每一条反馈。",
    keywords: "联系52吃瓜网,线索投稿,内容纠错,合作洽谈",
    path: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 52吃瓜网",
        description: "提交热点线索、内容纠错或合作咨询的联系页面。",
        url: "https://kimchy.cn/contact",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://kimchy.cn/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "https://kimchy.cn/contact" },
        ],
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    setStatus("submitting");
    setFormError("");

    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const succeeded = response.ok && parsed?.code === "OK";

      if (succeeded && !String(serverMsg).toLowerCase().includes("spam")) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          String(serverMsg || "").toLowerCase().includes("spam")
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败了，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络出现异常，请检查连接后重试。");
    }
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
        <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
          联系我们
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-loose text-foreground-600 font-label">
          无论是想提供一条线索、指出我们记录中的疏漏，还是洽谈内容合作，都可以通过下面的方式找到我们。每一条认真的反馈我们都会看。
        </p>
      </section>

      <section className="px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              联系渠道
            </h2>
            <div className="mt-4 flex flex-col gap-4">
              {channels.map((channel) => (
                <div
                  key={channel.title}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                    <i className={channel.icon}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">
                      {channel.title}
                    </h3>
                    <p className="mt-1 text-xs font-label text-primary-700">{channel.value}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-500 font-label">
                      {channel.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-secondary-900">
                <i className="ri-time-line"></i>
                回复时间说明
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-secondary-900/80 font-label">
                工作日提交的线索通常会在 2 个工作日内完成初步核实。若涉及正在快速发酵的话题，我们会优先处理并在对应事件记录中更新进展。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                提交线索或反馈
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500 font-label">
                带 <span className="text-primary-600">*</span> 的为必填项，留言请控制在 500 字以内。
              </p>

              {status === "success" ? (
                <div className="mt-6 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-5">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-xl text-secondary-600"></i>
                  <div>
                    <p className="font-heading text-sm font-bold text-secondary-900">
                      提交成功，感谢你的补充
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-secondary-900/80 font-label">
                      我们已收到你的信息，会尽快进行基础核实。如有需要补充的内容，可以再次提交。
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-3 cursor-pointer whitespace-nowrap rounded-full border border-secondary-300 px-4 py-1.5 text-xs font-label text-secondary-800 transition-colors hover:bg-secondary-100"
                    >
                      再提交一条
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  id="chigua-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-6 flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="contact-name"
                        className="text-sm font-label text-foreground-700"
                      >
                        称呼 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="怎么称呼你"
                        className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm font-label text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="contact-email"
                        className="text-sm font-label text-foreground-700"
                      >
                        邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="用于回复你的邮箱"
                        className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm font-label text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-type"
                      className="text-sm font-label text-foreground-700"
                    >
                      反馈类型 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="contact-type"
                      name="type"
                      required
                      defaultValue=""
                      className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm font-label text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                    >
                      <option value="" disabled>
                        请选择反馈类型
                      </option>
                      <option value="线索投稿">线索投稿</option>
                      <option value="内容纠错">内容纠错</option>
                      <option value="合作洽谈">合作洽谈</option>
                      <option value="其他建议">其他建议</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="contact-message"
                      className="text-sm font-label text-foreground-700"
                    >
                      具体内容 <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      placeholder="请尽量写明事件名称、发生时间和你掌握的信息来源，方便我们核实。"
                      className="resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm font-label leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                    ></textarea>
                    <span className="text-xs font-label text-foreground-400">
                      最多 500 字，请勿包含他人隐私信息。
                    </span>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot-alt"
                  />

                  {status === "error" && formError ? (
                    <div className="flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
                      <i className="ri-error-warning-line mt-0.5 text-base text-primary-600"></i>
                      <p className="text-xs leading-relaxed text-primary-900 font-label">
                        {formError}
                      </p>
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-relaxed text-foreground-400 font-label">
                      提交即表示你同意我们为核实内容而使用所填信息。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === "submitting" ? "提交中…" : "提交反馈"}
                    </button>
                  </div>
                </form>
              )}

              <p className="mt-6 border-t border-background-100 pt-4 text-xs leading-relaxed text-foreground-400 font-label">
                如果你只是想浏览今天的热门事件，可以回到
                <Link to="/hot" className="mx-1 text-primary-700 hover:text-primary-800">
                  今日热榜
                </Link>
                或
                <Link to="/" className="mx-1 text-primary-700 hover:text-primary-800">
                  首页
                </Link>
                继续浏览。
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}